import React from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ArrowRight } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Reveal from "../components/Reveal";
import { houseContent as c } from "../mock";

const House = () => {
  return (
    <div className="min-h-screen flex flex-col bg-background-ivory">
      <Header variant="dark" />

      {/* Cinematic full-bleed film hero */}
      <section className="relative w-full h-[92vh] min-h-[560px] overflow-hidden bg-brand-ink" data-testid="hero-film-section">
        <img
          src="/hero-still.webp"
          alt="A candlelit heritage banquet hall with crystal chandeliers and silver service"
          className="absolute inset-0 w-full h-full object-cover"
          decoding="async"
          data-testid="hero-still"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/45 to-black/20" />
        <div className="relative h-full flex items-center px-margin-mobile md:px-margin-desktop">
          <div className="max-w-2xl">
            <p
              className="font-inter text-eyebrow uppercase tracking-widest text-bright-gold-on-dark mb-6 animate-fade-in"
              style={{ opacity: 0, animationDelay: "400ms", animationDuration: "1.6s" }}
            >
              Pan India &amp; Abroad · A Hospitality House · Since 1997
            </p>
            <h1 className="font-cormorant text-[46px] leading-[1.02] md:text-[80px] text-ink-on-dark mb-8">
              <span className="block animate-fade-in" style={{ opacity: 0, animationDelay: "1100ms", animationDuration: "1.8s" }}>
                Not to be noticed.
              </span>
              <span
                className="block italic text-bright-gold-on-dark animate-fade-in"
                style={{ opacity: 0, animationDelay: "2300ms", animationDuration: "1.8s" }}
              >
                To be remembered.
              </span>
            </h1>
            <div
              className="w-16 h-px bg-brand-gold mb-8 animate-fade-in"
              style={{ opacity: 0, animationDelay: "3400ms", animationDuration: "1.4s" }}
            />
            <p
              className="font-cormorant text-body-lg text-ink-on-dark/80 max-w-md mb-10 animate-fade-in"
              style={{ opacity: 0, animationDelay: "3900ms", animationDuration: "1.6s" }}
            >
              Creating experiences beyond catering — private celebrations composed
              across India and abroad.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-3 border border-ink-on-dark/50 px-8 py-4 text-ink-on-dark hover:bg-ink-on-dark hover:text-brand-ink transition-all duration-500 animate-fade-in"
              style={{ opacity: 0, animationDelay: "4400ms", animationDuration: "1.4s" }}
            >
              <span className="font-inter text-label-sm uppercase tracking-widest">
                Begin a Conversation
              </span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
        <div
          className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-fade-in"
          style={{ opacity: 0, animationDelay: "5000ms", animationDuration: "1.4s" }}
          data-testid="hero-scroll-cue"
        >
          <ChevronDown className="w-6 h-6 text-ink-on-dark/70 animate-bounce" />
        </div>
      </section>

      {/* Welcome narrative */}
      <section className="px-margin-mobile md:px-margin-desktop py-narrative-gap">
        <div className="max-w-3xl mx-auto text-center">
          <Reveal
            as="p"
            className="font-cormorant text-[19px] md:text-[23px] leading-snug text-brand-ink"
          >
            {c.hero.body}
          </Reveal>
          <Reveal delay={200} className="w-12 h-px bg-brand-gold mx-auto mt-12" />
        </div>
      </section>

      {/* Who We Are */}
      <section className="px-margin-mobile md:px-margin-desktop pb-narrative-gap">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-gutter items-center">
          <Reveal className="col-span-1 md:col-span-6">
            <div className="w-full aspect-video overflow-hidden group bg-brand-ink">
              <video
                className="w-full h-full object-cover"
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                poster="/who-we-are-film-poster.jpg"
                data-testid="who-we-are-video"
              >
                <source src="/who-we-are-film.mp4" type="video/mp4" />
              </video>
            </div>
          </Reveal>
          <div className="col-span-1 md:col-span-5 md:col-start-8">
            <Reveal as="p" className="font-inter text-eyebrow text-brand-secondary uppercase mb-4">
              {c.whoWeAre.chapter}
            </Reveal>
            <Reveal
              as="h2"
              delay={100}
              className="font-cormorant text-[20px] md:text-[24px] leading-tight text-brand-ink mb-6"
            >
              {c.whoWeAre.title}
            </Reveal>
            <Reveal as="p" delay={200} className="font-cormorant text-body-md text-deep-taupe mb-12">
              {c.whoWeAre.body}
            </Reveal>
            <Reveal delay={300} className="border-t border-hairline-beige pt-8">
              <h3 className="font-cormorant text-[20px] md:text-[24px] leading-tight text-brand-ink mb-4">
                {c.hospitality.title}
              </h3>
              <p className="font-cormorant text-body-md text-deep-taupe">
                {c.hospitality.body}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="px-margin-mobile md:px-margin-desktop py-narrative-gap">
        <div className="max-w-6xl mx-auto">
          <Reveal as="p" className="font-inter text-eyebrow text-brand-secondary uppercase text-left mb-6">
            Our Philosophy
          </Reveal>
          <Reveal
            as="h2"
            delay={80}
            className="font-cormorant text-[20px] md:text-[24px] text-brand-ink text-left mb-16"
          >
            The belief behind the work.
          </Reveal>

          {[
            {
              n: "01",
              label: "The Belief",
              quote: "Hospitality is not a service. It is the feeling people take home with them.",
              body: "Founded in 1997 on a simple belief — people rarely remember every dish that was served, but they always remember how a celebration made them feel.",
            },
            {
              n: "02",
              label: "The Intent",
              quote: "Elegance is not about being noticed. It is about being remembered.",
              body: "We have never pursued trends. We have pursued timelessness — choosing relationships over transactions, and meaningful experiences over spectacle.",
            },
            {
              n: "03",
              label: "The Craft",
              quote: "True luxury is found in restraint, sincerity and uncompromising attention to detail.",
              body: "Extraordinary dining is not created through complexity alone, but through balance, restraint and intention. Every detail is intentional. Every gesture has purpose.",
            },
          ].map((p, i) => (
            <Reveal
              key={p.n}
              delay={i * 100}
              className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-gutter items-start border-t border-hairline-beige py-12"
            >
              <span className="md:col-span-3 font-inter text-label-sm uppercase tracking-widest text-brand-gold md:pt-2">
                {p.label}
              </span>
              <div className="md:col-span-9">
                <h3 className="font-cormorant text-[20px] md:text-[24px] leading-tight text-brand-ink mb-5">
                  {p.quote}
                </h3>
                <p className="font-cormorant text-body-md text-deep-taupe">{p.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Notable Evenings — a discreet statement of trust */}
      <section
        className="bg-brand-ink px-margin-mobile md:px-margin-desktop py-narrative-gap"
        data-testid="notable-evenings-section"
      >
        <div className="max-w-3xl mx-auto text-center">
          <Reveal
            as="p"
            className="font-inter text-eyebrow uppercase tracking-widest text-bright-gold-on-dark mb-6"
          >
            In Confidence
          </Reveal>
          <Reveal
            as="h2"
            delay={100}
            className="font-cormorant text-[26px] md:text-[38px] leading-tight text-ink-on-dark mb-8"
            data-testid="notable-evenings-heading"
          >
            Trusted for three decades by some of India&rsquo;s most discerning families.
          </Reveal>
          <Reveal delay={200} className="w-16 h-px bg-brand-gold mx-auto mb-8" />
          <Reveal
            as="p"
            delay={300}
            className="font-cormorant text-body-lg text-ink-on-dark/75"
          >
            From landmark weddings to private celebrations across India and abroad, we are
            welcomed into occasions we are not at liberty to name — and we hold that trust as
            our quietest achievement.
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default House;
